import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Autocomplete,
  useMediaQuery,
} from '@mui/material';
import {
  SendOutlined as SendIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ChatBubbleOutline as ChatIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown as ChevronIcon,
  AutoAwesome as SparkIcon,
} from '@mui/icons-material';
import mascotLarge from '../../assets/chatbot/mascot-large.svg';
import mascotSmall from '../../assets/chatbot/mascot-small.svg';
import userIcon from '../../assets/chatbot/user-icon.svg';
import { CHATBOT_SHORTCUTS } from '../../config/chatbotShortcuts';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setLoadingMessages,
  setLoadingOlderMessages,
  setSendingMessage,
  setError,
  setChats,
  appendChats,
  setSelectedChatId,
  setMessages,
  prependMessages,
  addMessage,
  setSearch,
  setChatPage,
  addChatToTop,
  deleteChat as deleteChatAction,
  clearMessages,
  setCurrentBotResponse,
  resetReactChatbot,
} from '../../store/slices/reactChatbotSlice';
import { GET_REACT_CHATBOT_CHATS, GET_REACT_CHATBOT_MESSAGES } from '../../graphql/queries/reactChatbot.queries';
import { GET_CHATBOT_SECTIONS_LIST } from '../../graphql/queries/chatbotSections.queries';
import { CHAT_WITH_REACT_BOT, DELETE_REACT_CHAT } from '../../graphql/mutations/reactChatbot.mutations';
import {
  FindReactChatListInput,
  FindReactMessageListInput,
  ReactChatsListResponse,
  ReactMessagesListResponse,
  ChatWithReactBotInput,
  ChatWithReactBotResponse,
  DeleteReactChatInput,
  DeleteReactChatResponse,
  ReactChat,
  ReactChatMessage,
  ReactBotResponse,
  BotOption,
  UserInput,
  ChatbotSectionOption,
} from '../../types/reactChatbot.types';
import {
  ChatbotSectionListResponse,
  FindChatbotSectionListInput,
} from '../../types/chatbotSections.types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MainLayout from '../../components/Layout/MainLayout';
import { reactChatbotLocales, ReactChatbotLang } from '../../locales/reactChatbot.locales';
import './ReactChatbot.css';

export type EmbedLang = ReactChatbotLang;
export type EmbedInitialView = 'chat' | 'list';

interface ReactChatbotProps {
  embedded?: boolean;
  sectionCode?: string | null;
  sessionExpired?: boolean;
  lang?: EmbedLang;
  initialView?: EmbedInitialView;
}

const NARROW_BREAKPOINT_PX = 768;

const ReactChatbot: React.FC<ReactChatbotProps> = ({
  embedded = false,
  sectionCode = null,
  sessionExpired = false,
  lang = 'es',
  initialView = 'chat',
}) => {
  const t = reactChatbotLocales[lang];

  const dispatch = useAppDispatch();
  const {
    chats,
    selectedChatId,
    messages,
    loading,
    loadingMessages,
    loadingOlderMessages,
    sendingMessage,
    error,
    chatsPagination,
    messagesPagination,
    search,
  } = useAppSelector((state) => state.reactChatbot);

  const [searchInput, setSearchInput] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Sections combobox state (only used in non-embedded mode)
  const [sections, setSections] = useState<ChatbotSectionOption[]>([]);
  const [selectedSection, setSelectedSection] = useState<ChatbotSectionOption | null>(null);

  // Embedded responsive state
  const isWide = useMediaQuery(`(min-width: ${NARROW_BREAKPOINT_PX}px)`);
  const [narrowView, setNarrowView] = useState<EmbedInitialView>(initialView);

  // In the narrow embed view, selection-table rows can be expanded to reveal
  // their columns as a key/value list. The set holds composite row keys
  // (`messageId::value::index`) so different messages don't collide.
  const compactMode = embedded && !isWide;
  const [expandedSelectRows, setExpandedSelectRows] = useState<Set<string>>(new Set());

  const toggleSelectRow = useCallback((rowKey: string) => {
    setExpandedSelectRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  }, []);

  // Delete state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<ReactChat | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Context menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuChatId, setMenuChatId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const chatListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ===== Queries & Mutations =====
  const [fetchChats] = useLazyQuery<
    { chatbotChats: ReactChatsListResponse },
    { params: FindReactChatListInput }
  >(GET_REACT_CHATBOT_CHATS, { fetchPolicy: 'network-only' });

  const [fetchMessages] = useLazyQuery<
    { chatbotMessages: ReactMessagesListResponse },
    { params: FindReactMessageListInput }
  >(GET_REACT_CHATBOT_MESSAGES, { fetchPolicy: 'network-only' });

  const [chatWithReactBotMutation] = useMutation<
    { chatWithReActBot: ChatWithReactBotResponse },
    { params: ChatWithReactBotInput }
  >(CHAT_WITH_REACT_BOT);

  const [deleteChatMutation] = useMutation<
    { deleteChatbotChat: DeleteReactChatResponse },
    { params: DeleteReactChatInput }
  >(DELETE_REACT_CHAT);

  const [fetchSections] = useLazyQuery<
    { chatbotSections: ChatbotSectionListResponse },
    { params: FindChatbotSectionListInput }
  >(GET_CHATBOT_SECTIONS_LIST, { fetchPolicy: 'network-only' });

  // ===== Resolved section code (prop in embed mode, autocomplete in admin mode) =====
  const resolvedSectionCode = embedded ? (sectionCode || null) : (selectedSection?.code || null);

  // ===== Normalize metadata (snake_case from DB → camelCase) =====
  const normalizeMetadata = (raw: any): ReactBotResponse | null => {
    if (!raw) return null;
    if ('inputType' in raw) return raw as ReactBotResponse;
    return {
      message: raw.message,
      inputType: raw.input_type ?? raw.inputType ?? 'text',
      options: raw.options ?? undefined,
      searchEnabled: raw.search_enabled ?? raw.searchEnabled ?? undefined,
      canRestart: raw.can_restart ?? raw.canRestart ?? true,
      currentStep: raw.current_step ?? raw.currentStep ?? undefined,
    };
  };

  // ===== Effects =====
  useEffect(() => {
    loadChats(1, false);
    if (!embedded) {
      loadSections();
    }
    return () => {
      dispatch(resetReactChatbot());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSections = async () => {
    try {
      const { data } = await fetchSections({
        variables: {
          params: {
            search: null,
            page: 1,
            pageSize: 1000,
            sort: { field: 'name', order: 'asc' },
          },
        },
      });
      if (data?.chatbotSections?.success && data.chatbotSections.data) {
        setSections(data.chatbotSections.data.sections.map((s) => ({
          id: s.id,
          code: s.code,
          name: s.name,
          description: s.description,
        })));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
    }
  };

  useEffect(() => {
    loadChats(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ===== Load chats =====
  const loadChats = async (page: number, append: boolean) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchChats({
        variables: {
          params: {
            search: search || null,
            page,
            pageSize: chatsPagination.pageSize,
          },
        },
      });
      if (data?.chatbotChats?.success && data.chatbotChats.data) {
        if (append) {
          dispatch(appendChats(data.chatbotChats.data));
        } else {
          dispatch(setChats(data.chatbotChats.data));
        }
        dispatch(setChatPage(page));
      } else {
        dispatch(setError(data?.chatbotChats?.message || 'Error al cargar chats'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // ===== Load messages for existing chat =====
  const loadMessages = async (chatId: string) => {
    try {
      dispatch(setLoadingMessages(true));
      const { data } = await fetchMessages({
        variables: {
          params: { chatId, search: null, page: 1, pageSize: 50 },
        },
      });
      if (data?.chatbotMessages?.success && data.chatbotMessages.data) {
        dispatch(setMessages(data.chatbotMessages.data));

        const msgs = [...data.chatbotMessages.data.messages].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        const lastAiMsg = [...msgs].reverse().find((m) => m.role === 'ai' && m.metadata);
        if (lastAiMsg?.metadata) {
          const botResp = normalizeMetadata(lastAiMsg.metadata);
          if (botResp) {
            dispatch(setCurrentBotResponse(botResp));
          }
        } else {
          dispatch(setCurrentBotResponse(null));
        }
      } else {
        dispatch(setError(data?.chatbotMessages?.message || 'Error al cargar mensajes'));
        dispatch(setLoadingMessages(false));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
      dispatch(setLoadingMessages(false));
    }
  };

  const loadOlderMessages = async () => {
    if (!selectedChatId || loadingOlderMessages || !messagesPagination.hasMorePages) return;
    const nextPage = messagesPagination.page + 1;
    try {
      dispatch(setLoadingOlderMessages(true));
      const el = messagesAreaRef.current;
      const prevScrollHeight = el?.scrollHeight || 0;
      const { data } = await fetchMessages({
        variables: {
          params: { chatId: selectedChatId, search: null, page: nextPage, pageSize: 50 },
        },
      });
      if (data?.chatbotMessages?.success && data.chatbotMessages.data) {
        dispatch(prependMessages(data.chatbotMessages.data));
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
        });
      } else {
        dispatch(setError(data?.chatbotMessages?.message || 'Error al cargar mensajes anteriores'));
        dispatch(setLoadingOlderMessages(false));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
      dispatch(setLoadingOlderMessages(false));
    }
  };

  // ===== Chat select =====
  const handleChatSelect = (chatId: string) => {
    if (embedded && !isWide) {
      setNarrowView('chat');
    }
    if (chatId === selectedChatId) return;
    dispatch(setSelectedChatId(chatId));
    dispatch(setCurrentBotResponse(null));
    loadMessages(chatId);
  };

  const handleNewConversation = () => {
    dispatch(clearMessages());
    setMessageInput('');
    if (embedded && !isWide) {
      setNarrowView('chat');
    }
  };

  const handleBackToList = () => {
    setNarrowView('list');
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSearch(value));
    }, 400);
  };

  // Infinite scroll
  const handleMessagesScroll = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    if (el.scrollTop < 50 && messagesPagination.hasMorePages && !loadingOlderMessages) {
      loadOlderMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesPagination.hasMorePages, loadingOlderMessages, selectedChatId, messagesPagination.page]);

  const handleChatListScroll = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 50 && chatsPagination.hasMorePages && !loading) {
      loadChats(chatsPagination.page + 1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatsPagination.hasMorePages, chatsPagination.page, loading]);

  // ===== Send message to ReAct bot =====
  const sendReactMessage = async (userMessage: string, userInput: UserInput | null) => {
    const userMsg: ReactChatMessage = {
      id: `temp-${Date.now()}`,
      chatId: selectedChatId || '',
      tenantId: '',
      userId: '',
      role: 'human',
      content: userMessage,
      feedback: null,
      metadata: userInput ? { userInput } : null,
      createdAt: new Date().toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      }),
      createdBy: null,
    };
    dispatch(addMessage(userMsg));
    dispatch(setSendingMessage(true));
    dispatch(setCurrentBotResponse(null));
    setMessageInput('');

    try {
      const { data: responseData } = await chatWithReactBotMutation({
        variables: {
          params: {
            chatId: selectedChatId || null,
            userMessage,
            userInput,
            sectionCode: resolvedSectionCode,
          },
        },
      });

      if (responseData?.chatWithReActBot?.success && responseData.chatWithReActBot.data) {
        const { chatId: returnedChatId, botResponse } = responseData.chatWithReActBot.data;

        const aiMsg: ReactChatMessage = {
          id: `ai-${Date.now()}`,
          chatId: returnedChatId,
          tenantId: '',
          userId: '',
          role: 'ai',
          content: botResponse.message,
          feedback: null,
          metadata: botResponse,
          createdAt: new Date().toLocaleString('en-US', {
            month: '2-digit', day: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
          }),
          createdBy: null,
        };
        dispatch(addMessage(aiMsg));
        dispatch(setCurrentBotResponse(botResponse));

        if (!selectedChatId && returnedChatId) {
          dispatch(setSelectedChatId(returnedChatId));
          const newChat: ReactChat = {
            id: returnedChatId,
            tenantId: '',
            userId: '',
            title: userMessage.length > 60 ? userMessage.substring(0, 60) + '...' : userMessage,
            lastMessageAt: aiMsg.createdAt,
            createdAt: aiMsg.createdAt,
            createdBy: null,
          };
          dispatch(addChatToTop(newChat));
        } else if (selectedChatId) {
          const existingChat = chats.find((c) => c.id === selectedChatId);
          if (existingChat) {
            dispatch(addChatToTop({ ...existingChat, lastMessageAt: aiMsg.createdAt }));
          }
        }
      } else {
        const msg = responseData?.chatWithReActBot?.message || 'Error al obtener respuesta del chatbot';
        dispatch(setError(msg));
      }
    } catch (err: any) {
      dispatch(setError(err.message || 'Error de conexión con el servidor'));
    } finally {
      dispatch(setSendingMessage(false));
    }
  };

  const handleOptionSelect = (option: BotOption) => {
    sendReactMessage(option.label, { type: 'option_select', value: option.value });
  };

  const handleSendTextInput = () => {
    const trimmed = messageInput.trim();
    if (!trimmed || sendingMessage) return;

    if (!selectedChatId && messages.length === 0) {
      sendReactMessage(trimmed, null);
    } else {
      sendReactMessage(trimmed, { type: 'text_input', value: trimmed });
    }
  };

  const handleSendShortcut = (text: string) => {
    if (!text || sendingMessage || sessionExpired) return;
    if (!selectedChatId && messages.length === 0) {
      sendReactMessage(text, null);
    } else {
      sendReactMessage(text, { type: 'text_input', value: text });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendTextInput();
    }
  };

  // ===== Context menu / delete =====
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, chatId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuChatId(chatId);
  };
  const handleMenuClose = () => { setMenuAnchorEl(null); setMenuChatId(null); };
  const handleDeleteClick = () => {
    const chat = chats.find((c) => c.id === menuChatId);
    if (chat) { setChatToDelete(chat); setConfirmDeleteOpen(true); setDeleteError(null); }
    handleMenuClose();
  };
  const handleCancelDelete = () => { setConfirmDeleteOpen(false); setChatToDelete(null); setDeleteError(null); };
  const handleConfirmDelete = async () => {
    if (!chatToDelete) return;
    try {
      setDeleting(true); setDeleteError(null);
      const { data: responseData } = await deleteChatMutation({
        variables: { params: { chatId: chatToDelete.id } },
      });
      if (responseData?.deleteChatbotChat?.success) {
        dispatch(deleteChatAction(chatToDelete.id));
        setConfirmDeleteOpen(false); setChatToDelete(null);
        setSuccessMessage(t.chatDeleted);
      } else {
        const msg = responseData?.deleteChatbotChat?.message || 'Error al eliminar chat';
        const errs = responseData?.deleteChatbotChat?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally { setDeleting(false); }
  };

  // In compact (narrow embed) mode, markdown tables coming from the bot are
  // re-rendered as a stack of key/value cards so columns don't get squashed in
  // the floating window. In wide / non-embed mode the table renders normally.
  const renderMarkdownTableAsCards = (children: React.ReactNode): React.ReactElement => {
    const sections = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement<any>[];
    let headers: React.ReactNode[] = [];
    const rows: React.ReactNode[][] = [];

    sections.forEach((section) => {
      const tag = (section.type as string) || section.props?.node?.tagName;
      const sectionRows = React.Children.toArray(section.props?.children).filter(React.isValidElement) as React.ReactElement<any>[];
      if (tag === 'thead') {
        const headerRow = sectionRows[0];
        if (headerRow) {
          headers = (React.Children.toArray(headerRow.props?.children).filter(React.isValidElement) as React.ReactElement<any>[])
            .map((cell) => cell.props?.children);
        }
      } else if (tag === 'tbody') {
        sectionRows.forEach((row) => {
          const cells = (React.Children.toArray(row.props?.children).filter(React.isValidElement) as React.ReactElement<any>[])
            .map((cell) => cell.props?.children);
          rows.push(cells);
        });
      }
    });

    if (rows.length === 0) {
      return <table>{children}</table>;
    }

    return (
      <div className="react-md-card-list">
        {rows.map((cells, ri) => (
          <div key={ri} className="react-md-card">
            {cells.map((cell, ci) => (
              <div key={ci} className="react-md-card-row">
                {headers[ci] !== undefined && (
                  <span className="react-md-card-label">{headers[ci]}</span>
                )}
                <span className="react-md-card-value">{cell}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const markdownComponents: any = compactMode
    ? { table: ({ children }: any) => renderMarkdownTableAsCards(children) }
    : undefined;

  // ===== Render bot message with controls =====
  const renderBotMessage = (msg: ReactChatMessage, isLastAiMessage: boolean) => {
    const metadata = normalizeMetadata(msg.metadata);
    const inputType = metadata?.inputType;
    const hasInteractiveControls = isLastAiMessage && !sendingMessage && !sessionExpired;

    return (
      <div key={msg.id} className="react-chat-message ai">
        <img src={mascotSmall} alt="" className="react-message-avatar" />
        <div>
          <div className="react-chat-message-bubble">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {msg.content}
            </ReactMarkdown>

            {inputType === 'options' && metadata?.options && metadata.options.length > 0 && (
              <Box className="react-bot-options" sx={{ mt: 1.5 }}>
                {metadata.options.map((opt: BotOption, idx: number) => (
                  <Button
                    key={`${opt.value}-${idx}`}
                    variant="outlined"
                    size="small"
                    className="react-option-button"
                    disabled={!hasInteractiveControls}
                    onClick={() => hasInteractiveControls && handleOptionSelect(opt)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '20px',
                      mr: 1,
                      mb: 1,
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Box>
            )}

            {inputType === 'dynamic_select' && metadata?.options && metadata.options.length > 0 && (() => {
              const hasColumns = metadata.options.some((opt: BotOption) => opt.columns && opt.columns.length > 0);
              const isInteractive = hasInteractiveControls;

              if (hasColumns) {
                const headers = metadata.options[0].columns!.map((col) => col.header);

                if (compactMode) {
                  return (
                    <Box className={`react-select-compact-wrapper ${!isInteractive ? 'disabled' : ''}`} sx={{ mt: 1.5 }}>
                      <ul className="react-select-compact-list">
                        {metadata.options.map((opt: BotOption, idx: number) => {
                          const rowKey = `${msg.id}::${opt.value}::${idx}`;
                          const isExpanded = expandedSelectRows.has(rowKey);
                          return (
                            <li key={rowKey} className="react-select-compact-item">
                              <button
                                type="button"
                                className="react-select-compact-row"
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? t.collapseDetailAria : t.expandDetailAria}
                                onClick={() => toggleSelectRow(rowKey)}
                              >
                                <span className="react-select-list-number">{idx + 1}</span>
                                <span className="react-select-compact-label">{opt.label}</span>
                                <span className={`react-select-compact-toggle ${isExpanded ? 'expanded' : ''}`}>
                                  <ChevronIcon fontSize="small" />
                                </span>
                              </button>
                              {isExpanded && (
                                <div className="react-select-compact-detail">
                                  {opt.columns!.map((col, cIdx) => (
                                    <div key={cIdx} className="react-select-compact-detail-row">
                                      <span className="react-select-compact-detail-label">
                                        {headers[cIdx]}
                                      </span>
                                      <span className="react-select-compact-detail-value">
                                        {col.value}
                                      </span>
                                    </div>
                                  ))}
                                  <Button
                                    variant="contained"
                                    size="small"
                                    fullWidth
                                    disabled={!isInteractive}
                                    onClick={() => isInteractive && handleOptionSelect(opt)}
                                    className="react-select-compact-confirm"
                                    sx={{
                                      mt: 1,
                                      textTransform: 'none',
                                      borderRadius: '20px',
                                      backgroundColor: 'var(--rc-primary, #667eea)',
                                      '&:hover': {
                                        backgroundColor: 'color-mix(in srgb, var(--rc-primary, #667eea) 88%, black)',
                                      },
                                    }}
                                  >
                                    {t.selectOption}
                                  </Button>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </Box>
                  );
                }

                return (
                  <Box className={`react-select-table-wrapper ${!isInteractive ? 'disabled' : ''}`} sx={{ mt: 1.5 }}>
                    <table className="react-select-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>{t.nameColumn}</th>
                          {headers.map((header, hIdx) => (
                            <th key={hIdx}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {metadata.options.map((opt: BotOption, idx: number) => (
                          <tr
                            key={`${opt.value}-${idx}`}
                            className={isInteractive ? 'clickable' : ''}
                            onClick={() => isInteractive && handleOptionSelect(opt)}
                          >
                            <td>{idx + 1}</td>
                            <td>{opt.label}</td>
                            {opt.columns!.map((col, cIdx) => (
                              <td key={cIdx}>{col.value}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                );
              } else {
                return (
                  <Box className={`react-select-list-wrapper ${!isInteractive ? 'disabled' : ''}`} sx={{ mt: 1.5 }}>
                    <ul className="react-select-list">
                      {metadata.options.map((opt: BotOption, idx: number) => (
                        <li
                          key={`${opt.value}-${idx}`}
                          className={isInteractive ? 'clickable' : ''}
                          onClick={() => isInteractive && handleOptionSelect(opt)}
                        >
                          <span className="react-select-list-number">{idx + 1}</span>
                          <span>{opt.label}</span>
                        </li>
                      ))}
                    </ul>
                  </Box>
                );
              }
            })()}

            {inputType === 'dynamic_select' && metadata?.options && metadata.options.length === 0 && (
              <Box sx={{ mt: 1.5, p: 2, textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                <Typography variant="body2">{t.noResults}</Typography>
              </Box>
            )}
          </div>
          <div className="react-chat-message-time">{msg.createdAt}</div>
        </div>
      </div>
    );
  };

  const lastAiMsg = [...messages].reverse().find((m) => m.role === 'ai');
  const lastAiMsgId = lastAiMsg?.id;

  // ===== Layout flags =====
  const showSidebar = !embedded || isWide || narrowView === 'list';
  const showChatPanel = !embedded || isWide || narrowView === 'chat';
  const showBackHeader = embedded && !isWide && narrowView === 'chat';

  // Title for narrow chat header (current chat title or "New conversation")
  const currentChatTitle = selectedChatId
    ? chats.find((c) => c.id === selectedChatId)?.title || t.newConversationTitle
    : t.newConversationTitle;

  const containerClassName = [
    'react-chatbot-container',
    embedded ? 'embedded' : '',
    embedded ? (isWide ? 'wide' : 'narrow') : '',
    embedded && !isWide ? `narrow-view-${narrowView}` : '',
  ].filter(Boolean).join(' ');

  const inputDisabled = sendingMessage || sessionExpired;

  const content = (
    <div className={containerClassName}>
      {/* ===== LEFT SIDEBAR ===== */}
      {showSidebar && (
        <div className="react-chat-sidebar">
          <div className="react-chat-sidebar-header">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              className="new-chat-button"
              onClick={handleNewConversation}
              disabled={sessionExpired}
            >
              {t.newConversation}
            </Button>
            {!embedded && (
              <Autocomplete
                size="small"
                options={sections}
                getOptionLabel={(option) => option.name}
                value={selectedSection}
                onChange={(_, newValue) => setSelectedSection(newValue)}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t.sectionPlaceholder} />
                )}
                fullWidth
              />
            )}
            <TextField
              size="small"
              placeholder={t.searchChat}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              fullWidth
              disabled={sessionExpired}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="react-chat-list-wrapper" ref={chatListRef} onScroll={handleChatListScroll}>
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={`react-chat-list-item ${selectedChatId === chat.id ? 'active' : ''}`}
                onClick={() => handleChatSelect(chat.id)}
              >
                <div className="react-chat-list-item-content">
                  <span className="react-chat-list-item-title">{chat.title}</span>
                  <span className="react-chat-list-item-date">{chat.lastMessageAt}</span>
                </div>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, chat.id)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                  disabled={sessionExpired}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            {loading && (
              <div className="react-chat-list-loading"><CircularProgress size={24} /></div>
            )}
            {!loading && chats.length === 0 && (
              <div className="react-chat-list-empty">{t.noConversations}</div>
            )}
          </div>
        </div>
      )}

      {/* ===== RIGHT - CHAT WINDOW ===== */}
      {showChatPanel && (
        <div className="react-chat-main">
          {showBackHeader && (
            <div className="react-chat-narrow-header">
              <IconButton
                size="small"
                onClick={handleBackToList}
                aria-label={t.backAria}
                className="react-chat-back-button"
              >
                <ArrowBackIcon />
              </IconButton>
              <span className="react-chat-narrow-title">{currentChatTitle}</span>
            </div>
          )}

          <div className="react-chat-messages-area" ref={messagesAreaRef} onScroll={handleMessagesScroll}>
            {!selectedChatId && messages.length === 0 && !loadingMessages ? (
              <div className="react-chat-empty-state">
                <div className="react-mascot-wrapper">
                  <div className="react-mascot-halo" />
                  <div className="react-mascot-ring">
                    <img src={mascotLarge} alt="" className="react-mascot-image" />
                  </div>
                </div>
                <div className="react-assistant-title">
                  <span className="react-assistant-title-text">{t.assistantTitle}</span>
                  <span className="react-assistant-badge">
                    <SparkIcon className="react-assistant-badge-spark" />
                    {t.assistantBadge}
                  </span>
                </div>
                <p className="react-assistant-subtitle">{t.botSubtitle}</p>
                <div className="react-shortcut-list">
                  {CHATBOT_SHORTCUTS.map((sc) => {
                    const Icon = sc.icon;
                    const label = t[sc.labelKey];
                    const message = t[sc.messageKey];
                    return (
                      <button
                        key={sc.labelKey}
                        type="button"
                        className="react-shortcut-button"
                        disabled={sendingMessage || sessionExpired}
                        onClick={() => handleSendShortcut(message)}
                      >
                        <Icon className="react-shortcut-icon" fontSize="small" />
                        <span className="react-shortcut-label">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : loadingMessages ? (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {loadingOlderMessages && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {messages.filter((msg) => msg.role !== 'tool').map((msg) => {
                  if (msg.role === 'human') {
                    return (
                      <div key={msg.id} className="react-chat-message human">
                        <div>
                          <div className="react-chat-message-bubble">{msg.content}</div>
                          <div className="react-chat-message-time">{msg.createdAt}</div>
                        </div>
                        <div className="react-message-avatar react-message-avatar-user">
                          <img src={userIcon} alt="" className="react-message-avatar-user-icon" />
                        </div>
                      </div>
                    );
                  }
                  return renderBotMessage(msg, msg.id === lastAiMsgId);
                })}

                {sendingMessage && (
                  <div className="react-typing-indicator">
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          <div className="react-chat-input-wrapper">
            <div className="react-chat-input-area">
              <TextField
                className="react-chat-input-field"
                placeholder={t.typeMessage}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={handleKeyDown}
                multiline
                maxRows={6}
                inputRef={inputRef}
                disabled={inputDisabled}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '24px',
                  },
                }}
              />
              <IconButton
                className="react-send-button"
                onClick={handleSendTextInput}
                disabled={!messageInput.trim() || inputDisabled}
              >
                <SendIcon />
              </IconButton>
            </div>
            <div className="react-chat-input-footer">
              <SparkIcon className="react-chat-input-footer-spark" />
              <span>{t.poweredByAI}</span>
            </div>
          </div>
        </div>
      )}

      {/* Session expired overlay (embed only) */}
      {embedded && sessionExpired && (
        <div className="react-chat-session-expired-overlay">
          <div className="react-chat-session-expired-card">
            <WarningIcon sx={{ fontSize: 48, color: '#e57373' }} />
            <Typography variant="h6" sx={{ mt: 1.5, fontWeight: 600 }}>
              {t.sessionExpiredTitle}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
              {t.sessionExpiredText}
            </Typography>
          </div>
        </div>
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>{t.deleteOption}</MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            {t.confirmDelete}
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography>{t.confirmDeleteText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} variant="outlined" disabled={deleting}>{t.cancel}</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? t.deleting : t.delete}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      {error && (
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => dispatch(setError(null))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => dispatch(setError(null))} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }
  return <MainLayout>{content}</MainLayout>;
};

export default ReactChatbot;
